import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Header } from "@/shared/components/Header";
import { Sidebar } from "@/shared/components/Sidebar";
import { MobileBottomTab } from "@/shared/components/MobileBottomTab";
import { CommandPalette } from "@/shared/components/CommandPalette";
import { AiConsentRepromptModal } from "@/shared/components/AiConsentRepromptModal";
import { useSessionStore } from "@/shared/session";
import { storeReturnTo } from "@/shared/oauth";
import { useUserMe } from "@/shared/api/queries";
import { useTranslation } from "@/shared/i18n/I18nProvider";

interface LayoutProps {
  /**
   * Dotted i18n key for the route's title (e.g. `"routes.dashboard"`).
   * Layout resolves it via `t()` so the header flips with locale.
   */
  titleKey: string;
}

/**
 * Routes that are direct destinations of the mobile bottom-tab. Anything not
 * in this set is treated as a "depth-2" screen on mobile and gets the
 * collapsing back button + hidden bottom tab.
 */
const DEPTH1_PATHS = new Set([
  "/dashboard",
  "/products",
  "/projects",
  "/settings",
]);

/**
 * Resolve the i18n key for the parent route's title. Used as `{{parent}}` in
 * the in-content `common.backTo` label ("Quay lại {parent}").
 *
 * Mapping:
 *   /settings/*                                    → routes.settings
 *   /products/:id                                  → routes.products
 *   /projects/:id/(editor|render|export)           → routes.projectDetailParent
 *   /projects/:id  (bare)                          → routes.clips
 *   fallback                                       → routes.dashboard
 */
function resolveParentTitleKey(pathname: string): string {
  if (pathname.startsWith("/settings/")) return "routes.settings";
  if (/^\/products\/[^/]+\/?$/.test(pathname)) return "routes.products";
  if (/^\/projects\/[^/]+\/(editor|render|export)\/?$/.test(pathname)) {
    return "routes.projectDetailParent";
  }
  if (/^\/projects\/[^/]+\/?$/.test(pathname)) return "routes.clips";
  return "routes.dashboard";
}

/**
 * Best-effort parent path used as the back-button fallback when the browser
 * history pointer is at idx 0 (deep-link / refresh — `navigate(-1)` would
 * exit the app).
 */
function resolveParentPath(pathname: string): string {
  if (pathname.startsWith("/settings/")) return "/settings";
  if (/^\/products\/[^/]+\/?$/.test(pathname)) return "/products";
  const projDetail = pathname.match(
    /^(\/projects\/[^/]+)\/(?:editor|render|export)\/?$/,
  );
  if (projDetail) return projDetail[1];
  if (/^\/projects\/[^/]+\/?$/.test(pathname)) return "/projects";
  return "/dashboard";
}

/**
 * App-shell layout.
 *
 * Responsive contract:
 *   - md+ (≥ 768px): Sidebar (240px) + Header + main content. No back chrome.
 *   - sm-only (< 768px):
 *       * depth-1 (`/dashboard`, `/products`, `/projects`, `/settings`) →
 *         Header + main + MobileBottomTab (visible).
 *       * depth-2 (everything else) → MobileBottomTab slides off-screen, an
 *         in-content back-link sits above page content; once that link scrolls
 *         out of view an `IntersectionObserver` flips the Header back chevron
 *         on. Cross-fade is mutually exclusive by construction.
 *
 * Theme-aware: main background and surrounding chrome use semantic token
 * classes (bg-canvas / text-fg-muted) so dark mode flips the entire shell,
 * not just card contents.
 *
 * i18n-aware: `titleKey` flows through `t()` so the page title in the header
 * flips when the user switches locale in Settings; `common.backTo` is
 * interpolated with the resolved parent title.
 */
export function Layout({ titleKey }: LayoutProps) {
  const session = useSessionStore((s) => s.session);
  const me = useUserMe();
  const { t, syncFromAccount } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // F-03 — adopt the user's account locale (the one they picked on mobile) on
  // web, unless they've explicitly chosen a locale on this device. Without this
  // a VN user on an en-US browser sees an English web app despite choosing vi.
  useEffect(() => {
    if (me.data?.locale) syncFromAccount(me.data.locale);
  }, [me.data?.locale, syncFromAccount]);

  // FL-4 — keep the browser-tab title in sync with the current route and
  // the active locale. Runs on every route/locale change.
  useEffect(() => {
    document.title = `${t(titleKey)} · iSale`;
  }, [titleKey, t]);

  const isDepth1 = DEPTH1_PATHS.has(location.pathname);
  const isDepth2 = !isDepth1;

  // Binary "is the in-content back-link still in the scroll viewport" signal.
  // IntersectionObserver gives this to us without picking a magic px threshold.
  const backLinkRef = useRef<HTMLButtonElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const [contentBackVisible, setContentBackVisible] = useState(true);

  // Reset to "visible" on every navigation so we never freeze the header back
  // ON when navigating into a fresh depth-2 screen (scroll always resets too).
  useEffect(() => {
    setContentBackVisible(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDepth2) return;
    const link = backLinkRef.current;
    const root = mainRef.current;
    if (!link || !root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.target === link) {
            setContentBackVisible(e.isIntersecting);
          }
        }
      },
      { root, threshold: 0 },
    );
    obs.observe(link);
    return () => obs.disconnect();
  }, [isDepth2, location.pathname]);

  const parentTitleKey = useMemo(
    () => resolveParentTitleKey(location.pathname),
    [location.pathname],
  );

  const handleBack = () => {
    // `window.history.state.idx` is React Router DOM's internal index; `0`
    // means there's no prior in-app entry (deep link / page refresh), so
    // `navigate(-1)` would punt out of the SPA — fall back to a sensible
    // parent route instead.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx === 0) {
      navigate(resolveParentPath(location.pathname));
    } else {
      navigate(-1);
    }
  };

  if (!session) {
    // FL-2: save the intended destination so SignIn can pass it through the
    // OAuth flow → OAuthCallback navigates back here after successful sign-in.
    storeReturnTo(location.pathname + location.search);
    return <Navigate to="/sign-in" replace />;
  }

  const headerBackVisible = isDepth2 && !contentBackVisible;

  return (
    <div
      // `h-dvh` (dynamic viewport) — corrects the iOS / mobile WebView quirk
      // where `h-screen` (= 100vh) reports the URL-bar-collapsed height, so a
      // second body scrollbar appears alongside main's when the bar is shown.
      className="flex h-dvh overflow-hidden bg-canvas"
    >
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          title={t(titleKey)}
          showBack={headerBackVisible}
          onBack={handleBack}
        />
        <main
          ref={mainRef}
          // depth-1 keeps `pb-24` so the last row clears MobileBottomTab;
          // depth-2 drops to `pb-8` since the bar is slid off-screen.
          className={
            isDepth2
              ? "flex-1 overflow-y-auto overscroll-contain bg-canvas p-4 pb-8 sm:p-6 md:p-8"
              : "flex-1 overflow-y-auto overscroll-contain bg-canvas p-4 pb-24 sm:p-6 md:p-8 md:pb-8"
          }
        >
          {isDepth2 && (
            <button
              ref={backLinkRef}
              type="button"
              onClick={handleBack}
              data-testid="layout-content-back"
              className="mb-4 flex items-center gap-1 text-sm font-bold text-fg-muted md:hidden"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("common.backTo", { parent: t(parentTitleKey) })}
            </button>
          )}
          <Outlet />
        </main>
      </div>
      <MobileBottomTab hidden={isDepth2} />
      <CommandPalette />
      <AiConsentRepromptModal />
    </div>
  );
}
